import type {
  TradingGroupJobEntry,
  TradingGroupJobNotification,
  TradingGroupJobsState,
} from './slice';

export interface TradingGroupJobsStore {
  tradingGroupJobs: TradingGroupJobsState;
}

/** Settled jobs waiting to be announced by the notification UI. */
export const selectTradingGroupJobNotifications = (
  store: TradingGroupJobsStore,
): TradingGroupJobNotification[] => store.tradingGroupJobs.notifications;

/** The tracked entry for a jobId, or `undefined` if it isn't tracked (unknown or already settled). */
export const selectTradingGroupJobEntry = (
  store: TradingGroupJobsStore,
  jobId?: string,
): TradingGroupJobEntry | undefined =>
  jobId != null ? store.tradingGroupJobs.byJobId[jobId] : undefined;

/**
 * All currently tracked (in-flight) jobs, keyed by jobId — drives the
 * persistent "in progress" notification's started/progress phases. Same
 * object reference across renders until a job starts, progresses, or
 * settles, so consumers can safely diff against a previous render's map.
 */
export const selectTradingGroupJobEntries = (
  store: TradingGroupJobsStore,
): Record<string, TradingGroupJobEntry> => store.tradingGroupJobs.byJobId;

/** True while the given jobId has a tracked, in-flight job. */
export const selectIsTradingGroupJobPending = (
  store: TradingGroupJobsStore,
  jobId?: string,
): boolean => jobId != null && Boolean(store.tradingGroupJobs.byJobId[jobId]);

/**
 * True while any tracked job's `groupId` matches, or (for a `bulk-archive`/
 * `bulk-delete`/explicit-`groupIds` bulk-members job) `groupIds` includes it
 * — drives the group **record**'s pending/disabled state (archive, delete,
 * rename, change-bot). Deliberately does *not* treat a `scope`-targeted
 * `bulk-add-members`/`bulk-remove-members` job (SKY-4559) as pending for
 * every group: such a job only ever touches membership, never the group
 * record itself, so archive/delete/rename/change-bot elsewhere in the app
 * have nothing to wait for — blocking them for the sweep's whole (often
 * many-minutes) duration would be a needless, unrelated regression. See
 * `selectIsTradingGroupMembersPending` for the members-tab counterpart that
 * *does* treat such a job as pending everywhere.
 */
export const selectIsTradingGroupPending = (
  store: TradingGroupJobsStore,
  groupId?: number,
): boolean =>
  groupId != null &&
  Object.values(store.tradingGroupJobs.byJobId).some(
    (entry) => entry.groupId === groupId || entry.groupIds?.includes(groupId),
  );

/**
 * Same as `selectIsTradingGroupPending`, plus: true while a `scope`-targeted
 * `bulk-add-members`/`bulk-remove-members` job (SKY-4559) is in flight,
 * regardless of `groupId`. Such a job carries neither `groupId` nor
 * `groupIds` — its target set is resolved server-side, never known to this
 * client — so it's treated as pending for every group rather than none: an
 * offboarding sweep in flight must disable per-row member actions everywhere
 * it could plausibly still touch, not just the groups this client happens to
 * know about. Drives `TradingGroupMembersTab`'s "Add members"/"Remove"
 * disabled state specifically — not group-record actions, which stay on
 * `selectIsTradingGroupPending` above.
 */
export const selectIsTradingGroupMembersPending = (
  store: TradingGroupJobsStore,
  groupId?: number,
): boolean =>
  groupId != null &&
  Object.values(store.tradingGroupJobs.byJobId).some(
    (entry) =>
      entry.groupId === groupId ||
      entry.groupIds?.includes(groupId) ||
      ((entry.operation === 'bulk-add-members' || entry.operation === 'bulk-remove-members') &&
        entry.groupIds === undefined),
  );
